/** @jsxImportSource react */
import React, { forwardRef } from 'react';
import { Order, User } from '../types';
import { useTranslation } from '../i18n';
import { Tag, MapPin, Phone, Mail, Globe } from 'lucide-react';

interface InvoiceTemplateProps {
    order: Order;
    client: User;
}

export const InvoiceTemplate = forwardRef<HTMLDivElement, InvoiceTemplateProps>(({ order, client }, ref) => {
    const { t, formatDate, formatCurrency } = useTranslation();
    const subtotal = (order.finalPrice || 0) - (order.commission || 0);

    // A4 dimensions in mm: 210 x 297.
    // Using minHeight 295mm (slightly less than 297) to prevent sub-pixel overflow creating a second blank page.
    // Using flex-col to push footer to bottom naturally.
    return (
        <div ref={ref} style={{ width: '210mm', minHeight: '295mm', margin: '0 auto', backgroundColor: 'white' }} className="flex flex-col font-sans text-slate-900 leading-normal relative">
            {/* Header - Dark Theme */}
            <div className="bg-slate-900 text-white p-10 relative overflow-hidden shrink-0 print:bg-slate-900 print:text-white">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
                 <div className="relative z-10 flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <Tag className="h-8 w-8 text-primary-400 transform -rotate-45" />
                            <span className="text-3xl font-bold tracking-tight text-white">{t('tagExpress')}</span>
                        </div>
                        <p className="text-slate-400 text-sm mt-2 max-w-xs leading-relaxed">
                            {t('globalSourcingLogistics')}
                        </p>
                    </div>
                    <div className="text-right">
                        <h1 className="text-4xl font-bold text-white mb-1 uppercase tracking-widest opacity-90">{t('invoice')}</h1>
                        <p className="text-primary-400 font-mono text-lg mb-4">#{order.orderId}</p>
                        <div className={`inline-block px-3 py-1 rounded text-xs font-bold uppercase tracking-wider border ${order.paymentStatus === 'Paid' || order.paymentStatus === 'Verified' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-amber-500/20 text-amber-300 border-amber-500/30'}`}>
                            {t(order.paymentStatus.toLowerCase())}
                        </div>
                    </div>
                 </div>
            </div>

            <div className="p-10 flex-grow">
                {/* Client & Invoice Info */}
                <div className="flex justify-between mb-10 border-b border-slate-100 pb-8">
                    <div className="w-1/2 pr-4">
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('billTo')}</h3>
                        <p className="text-xl font-bold text-slate-900 mb-1">{client.name}</p>
                        <div className="text-sm text-slate-600 space-y-1">
                            <p className="flex items-center gap-2">
                                <Mail className="w-3 h-3 text-slate-400" /> {client.email}
                            </p>
                            {client.phone && (
                                <p className="flex items-center gap-2">
                                    <Phone className="w-3 h-3 text-slate-400" /> {client.countryCode} {client.phone}
                                </p>
                            )}
                            <p className="flex items-center gap-2">
                                <MapPin className="w-3 h-3 text-slate-400" /> {client.city || 'N/A'}
                            </p>
                        </div>
                    </div>
                    <div className="w-1/2 pl-4 text-right">
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">{t('invoiceDetails')}</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between md:justify-end gap-6">
                                <span className="text-slate-500">{t('dateOfIssue')}:</span>
                                <span className="font-medium text-slate-900">{formatDate(new Date())}</span>
                            </div>
                            <div className="flex justify-between md:justify-end gap-6">
                                <span className="text-slate-500">{t('orderDate')}:</span>
                                <span className="font-medium text-slate-900">{formatDate(order.createdAt)}</span>
                            </div>
                             <div className="flex justify-between md:justify-end gap-6">
                                <span className="text-slate-500">{t('orderType')}:</span>
                                <span className="font-medium text-slate-900">{t(order.orderType.toLowerCase())}</span>
                            </div>
                             {order.trackingNumber && (
                                <div className="flex justify-between md:justify-end gap-6">
                                    <span className="text-slate-500">{t('trackingNumber')}:</span>
                                    <span className="font-medium text-slate-900">{order.trackingNumber}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Shipment / Product Details */}
                <div className="mb-10 bg-slate-50 p-6 rounded-lg border border-slate-100 print:bg-slate-50">
                     <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 border-b border-slate-200 pb-2">{t('orderDetails')}</h3>
                     <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
                        <div>
                            <span className="block text-xs text-slate-400 mb-1">{t('product')}</span>
                            <span className="font-medium text-slate-800">{order.productName}</span>
                        </div>
                        {order.shippingType && (
                            <div>
                                <span className="block text-xs text-slate-400 mb-1">{t('shippingMethod')}</span>
                                <span className="font-medium text-slate-800">{t(order.shippingType.toLowerCase())}</span>
                            </div>
                        )}
                         {order.weight && (
                            <div>
                                <span className="block text-xs text-slate-400 mb-1">{t('weight')}</span>
                                <span className="font-medium text-slate-800">{order.weight} kg</span>
                            </div>
                        )}
                        {order.dimensions && (
                            <div>
                                <span className="block text-xs text-slate-400 mb-1">{t('dimensions')}</span>
                                <span className="font-medium text-slate-800">{order.dimensions}</span>
                            </div>
                        )}
                         {order.sourceWarehouseId && (
                            <div>
                                <span className="block text-xs text-slate-400 mb-1">{t('source')}</span>
                                <span className="font-medium text-slate-800">{order.sourceWarehouseId}</span>
                            </div>
                        )}
                     </div>
                </div>

                {/* Table */}
                <div className="mb-10">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="border-b-2 border-slate-100">
                                <th className="text-left py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('description')}</th>
                                <th className="text-center py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('quantity')}</th>
                                <th className="text-right py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">{t('amount')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-slate-50">
                                <td className="py-4 text-sm font-medium text-slate-900">
                                    {order.productName}
                                    <div className="text-xs text-slate-500 font-normal mt-1">{order.notes}</div>
                                </td>
                                <td className="py-4 text-center text-sm text-slate-600">{order.quantity}</td>
                                <td className="py-4 text-right text-sm font-bold text-slate-900">{formatCurrency(order.finalPrice)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div className="flex justify-end mb-12">
                    <div className="w-5/12 space-y-3 pt-2 border-t border-slate-100">
                        <div className="flex justify-between text-sm text-slate-600">
                            <span>{t('subtotal')}</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>
                        {order.commission && order.commission > 0 && (
                            <div className="flex justify-between text-sm text-slate-600">
                                <span>{t('commission')}</span>
                                <span>{formatCurrency(order.commission)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-lg font-bold text-slate-900 pt-3 border-t border-slate-200">
                            <span>{t('total')}</span>
                            <span className="text-primary-700">{formatCurrency(order.finalPrice)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer - Pushed to bottom via flex-grow on content above */}
            <div className="bg-slate-50 border-t border-slate-200 p-8 shrink-0 print:bg-slate-50 mt-auto">
                <div className="flex justify-between items-end text-xs text-slate-500">
                     <div>
                        <p className="font-bold text-slate-700 mb-1">{t('thankYouForYourBusiness')}</p>
                        <p>{t('contactSupportInquiry')}</p>
                    </div>
                    <div className="text-right space-y-1">
                        <p className="flex items-center justify-end gap-2"><Globe className="w-3 h-3" /> www.tagexpress.com</p>
                        <p className="flex items-center justify-end gap-2"><Mail className="w-3 h-3" /> contact@tagexpress.com</p>
                        <p className="flex items-center justify-end gap-2"><MapPin className="w-3 h-3" /> Sulaymaniyah, Iraq</p>
                    </div>
                </div>
            </div>
        </div>
    );
});

InvoiceTemplate.displayName = 'InvoiceTemplate';